import * as SurveyDefinitionActions from "src/app/store/survey/actions";
import * as FromSurveyDefinition from "src/app/store/survey/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit } from "@angular/core";
import { AlertController, NavController, PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Camera, CameraResultType, CameraSource, Photo } from "@capacitor/camera";
import { StorageService } from "src/app/services/storage/storage.service";
import { combineLatest, merge, Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { SurveyFieldDto, CreateSurveyFieldDto, SurveyDefinitionDto, UpdateSurveyFieldDto } from "@api";
import { first, mergeMap, skipWhile, withLatestFrom } from "rxjs/operators";

@Component({
  selector: "app-survey-field-form",
  templateUrl: "./survey-field-form.page.html",
  styleUrls: ["./survey-field-form.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyFieldFormPage implements OnInit {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public form: FormGroup;
  public image?: Photo;
  public breadcrumbs: any[] = [];
  public readonly loading$: Observable<boolean> = this.store.select(FromSurveyDefinition.selectLoading);
  public readonly surveyDefinitionSlug$: Observable<string> = this.store.select(FromRouter.selectSurveySlug);
  public readonly surveyDefinition$: Observable<SurveyDefinitionDto> = this.surveyDefinitionSlug$.pipe(
    mergeMap(definitionSlug => this.store.select(FromSurveyDefinition.selectDefinitionBySlug(definitionSlug))),
  );
  public readonly surveyFieldId$: Observable<string> = this.store.select(FromRouter.selectSurveyFieldId);
  public readonly surveyField$: Observable<SurveyFieldDto> = combineLatest([this.surveyDefinitionSlug$, this.surveyFieldId$]).pipe(
    mergeMap(([surveyDefinitionSlug, surveyFieldId]) =>
      this.store
        .select(FromSurveyDefinition.selectDefinitionBySlug(surveyDefinitionSlug))
        .pipe(
          mergeMap(surveyDefinition => this.store.select(FromSurveyDefinition.selectDefinitionField(surveyDefinition?.id, surveyFieldId))),
        ),
    ),
  );
  public surveyFields$: Observable<SurveyFieldDto[]> = this.surveyDefinitionSlug$.pipe(
    mergeMap(definitionSlug => this.store.select(FromSurveyDefinition.selectDefinitionFieldsBySlug(definitionSlug))),
  );
  public readonly availableTypes: SurveyFieldDto.TypeEnum[] = Object.values(SurveyFieldDto.TypeEnum);
  public defaultHref: string;

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly navController: NavController,
    private readonly storage: StorageService,
  ) {}

  public ngOnInit(): void {
    this.form = new FormGroup({
      type: new FormControl(null, [Validators.required]),
      question: new FormControl(null, [Validators.required]),
      description: new FormControl(null, [Validators.required]),
      order: new FormControl(1, [Validators.required]),
      required: new FormControl(false, [Validators.required]),
      options: new FormControl(null),
      defaultValue: new FormControl(null, [Validators.required]),
      min: new FormControl(null),
      max: new FormControl(null),
      helpText: new FormControl(null, [Validators.required]),
      helpUrl: new FormControl(null, [Validators.required]),
    });
  }

  public cancel(): void {
    this.navController.back();
  }

  public async loadImage(): Promise<void> {
    this.image = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Base64,
      quality: 100,
    });

    this.form.markAsDirty();
    this.changeDetector.detectChanges();
  }

  public async removeImage(): Promise<void> {
    if (this.image) this.image = null;
    this.surveyField$.pipe(first()).subscribe(async surveyField => {
      if (surveyField.imageUrl) {
        await this.storage.removeFromUrl(surveyField.imageUrl);
        this.update({ ...surveyField, imageUrl: null }, false);
      }
    });
  }

  public async save(): Promise<void> {
    const options = this.form.value.options
      ? [...new Set(this.form.value.options?.split(",").map((option: string) => option.trim()))]
      : null;
    this.store.dispatch(SurveyDefinitionActions.setLoading());
    let imageUrl: string;
    if (this.image) imageUrl = await this.storage.upload(this.image);
    const payload: CreateSurveyFieldDto = {
      ...this.form.value,
      imageUrl,
      options,
    };
    this.surveyFieldId$.pipe(first()).subscribe(surveyFieldId => {
      if (surveyFieldId) this.update(payload, true);
      else this.create(payload);
    });
  }

  protected ionViewWillEnter(): void {
    combineLatest([this.surveyDefinition$, this.surveyField$, this.surveyFieldId$])
      .pipe(
        skipWhile(([surveyDefinition, surveyField, surveyFieldId]) => {
          if (surveyFieldId) return !surveyField || !surveyDefinition;
          return !surveyDefinition;
        }),
        first(),
      )
      .subscribe(([surveyDefinition, surveyField]) => {
        this.breadcrumbs = [
          { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${AppRoutes.home}` },
          { name: this.i18n.instant("ADMIN_SURVEYS.BREADCRUMB_NAME"), link: `/${AppRoutes.adminSurveyDefinitions}` },
          { name: surveyDefinition.name, link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}` },
        ];
        this.defaultHref = `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}`;
        if (surveyField) {
          this.breadcrumbs.push(
            ...[
              {
                name: surveyField.id,
                link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}/${AppRoutes.surveyFields}/${surveyField.id}`,
              },
              {
                name: this.i18n.instant("ADMIN_SURVEYS.EDIT_FIELD"),
                link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}/${AppRoutes.surveyFields}/${surveyField.id}/${AppRoutes.edit}`,
              },
            ],
          );
          this.form.patchValue({ ...surveyField });
        } else {
          this.breadcrumbs.push({
            name: this.i18n.instant("ADMIN_SURVEYS.ADD_FIELD"),
            link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}/${AppRoutes.surveyFields}/${AppRoutes.add}`,
          });
        }
        this.changeDetector.detectChanges();
      });

    combineLatest([this.surveyFields$, this.surveyFieldId$])
      .pipe(
        skipWhile(([surveyFields]) => !surveyFields.length),
        first(),
      )
      .subscribe(([surveyFields, surveyFieldId]) => {
        if (!surveyFieldId) this.form.patchValue({ order: surveyFields.length + 1 });
      });
  }

  protected ionViewDidEnter(): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(slug => {
      this.store.dispatch(SurveyDefinitionActions.getDefinitionBySlug({ slug }));
    });
  }

  private create(createSurveyFieldDto: CreateSurveyFieldDto): void {
    this.surveyDefinition$.pipe(first()).subscribe(surveyDefinition => {
      this.store.dispatch(
        SurveyDefinitionActions.createSurveyField({
          definitionId: surveyDefinition.id,
          slug: surveyDefinition.slug,
          createSurveyFieldDto: {
            ...createSurveyFieldDto,
            definitionId: surveyDefinition.id,
          },
        }),
      );
    });
  }

  private update(updateSurveyFieldDto: UpdateSurveyFieldDto, goBack: boolean): void {
    combineLatest([this.surveyDefinition$, this.surveyFieldId$])
      .pipe(first())
      .subscribe(([definition, fieldId]) => {
        this.store.dispatch(
          SurveyDefinitionActions.updateSurveyField({
            definitionId: definition.id,
            fieldId,
            slug: definition.slug,
            updateSurveyFieldDto,
            goBack,
          }),
        );
      });
  }
}
